// src/components/layout/TopNavBarApp.tsx
// -----------------------------------------------------------------------------
// TopNavBarApp
// -----------------------------------------------------------------------------
// - Fixed top navigation bar for authenticated app pages
// - Features:
//   • Back arrow (when not on /dashboard) inside a glass circle
//   • Centered brand title ("Athena")
//   • Right-side controls: language selector + user avatar
//   • Dropdown menus for language and user actions
// - Styled with glassmorphic aesthetics to match app design system
// -----------------------------------------------------------------------------

import {
  ArrowBack,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Check as CheckIcon,
  Language as LanguageIcon,
  School as TutorialIcon,
  FolderShared as EnvironmentsIcon,
  LockOutlined as LockIcon,
  AdminPanelSettings as AdminPanelIcon,
  VerifiedUser as VerifiedUserIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Divider,
  Fade,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { startTransition, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { DASHBOARD_PATH } from "../dashboards/dashboardSurface";
import NavPatientSearch from "./NavPatientSearch";
import { RootState } from "types";
import { logout } from "../../redux/authSlice";
import { useThemeContext } from "../../theme/ThemeContext";
import { athenaColors } from "../../theme/palette";
import NotificationBell from "./NotificationBell";
import ContractsNavChip from "../legal/ContractsNavChip";
import ContractsWizardModal from "../legal/ContractsWizardModal";
import { useContractsStatus } from "../../hooks/useContractsStatus";
import { TUTORIAL_ENABLED, useTutorial } from "hooks/useOnboardingTutorial";
// i18n - Internationalization
import { useLanguageSwitcher, useTranslation, SupportedLanguage } from "../../i18n";
// Tier gating
import { useFeatureGate } from "../../hooks/useFeatureGate";

const TopNavBarApp = () => {
  const { t } = useTranslation();
  const { language, setLanguage, languages, currentLanguage } = useLanguageSwitcher();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const location = useLocation();
  // On /dashboard ONLY, the bar is not its own opaque navy band but a
  // translucent shelf on the page ground, so the ground and the bloom in it
  // run behind it. It is still a bar: 55% navy, a blur, and a periwinkle
  // hairline underneath. Fully transparent was wrong, the bar dissolved into
  // the ground and the page looked like it had lost its chrome.
  const openNav = location.pathname === DASHBOARD_PATH;
  // The bar carries a patient typeahead on the patient-details path: deep
  // inside one patient there is otherwise no way to reach another without
  // going back to the dashboard first.
  const navSearch = /^\/patients\/[^/]+\/details$/.test(location.pathname);
  const theme = useTheme();
  const dispatch = useDispatch();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const tutorial = useTutorial();
  const { quotas } = useFeatureGate();
  const canManageTeam = quotas.max_users > 1; // Professional+ tiers
  const contractsStatus = useContractsStatus();
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [hasActiveTasks, setHasActiveTasks] = useState(false);
  const [taskProgress, setTaskProgress] = useState(-1); // -1 = indeterminate
  const handleActiveTasksChange = useCallback((active: boolean, progress: number) => {
    setHasActiveTasks(active);
    setTaskProgress(active ? progress : -1);
  }, []);

  const linkedinBotUrl =
    process.env.NODE_ENV === "development"
      ? "http://localhost:3000/linkedin-bot/start"
      : "/linkedin-bot/start";

  // Theme context for switching
  const { themeVariant, setThemeVariant, themeOptions } = useThemeContext();

  // User state
  const userName = currentUser?.username || currentUser?.email || "";
  const userInitial = userName ? userName.charAt(0).toUpperCase() : "";
  const isSystemAdmin = currentUser?.is_system_admin === true;
  // Joan, 2026-05-27: gatekeeper role for the bulk env-memberships page.
  // Includes superadmins (who already have all powers) + non-superadmin
  // env admins like Sebastian.
  const isEnvGatekeeper =
    isSystemAdmin || currentUser?.is_environment_admin === true;

  // Menu state
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [langMenuAnchor, setLangMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [themeMenuAnchor, setThemeMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  // Handlers
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
    setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const handleLangMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
    setLangMenuAnchor(event.currentTarget);
  const handleLangMenuClose = () => setLangMenuAnchor(null);

  const handleThemeMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setThemeMenuAnchor(event.currentTarget);
  const handleThemeMenuClose = () => setThemeMenuAnchor(null);

  const handleLogout = async () => {
    try {
      const { default: api } = await import("../../api/api");
      await api.post("/billing/log-logout/");
    } catch {
      // Best-effort — don't block logout if the call fails
    }
    dispatch(logout());
    navigate("/login");
  };

  const changeLanguage = (lng: SupportedLanguage) => {
    startTransition(() => {
      setLanguage(lng);
    });
    handleLangMenuClose();
  };

  const changeTheme = (variant: "athena_gradient") => {
    setThemeVariant(variant);
    handleThemeMenuClose();
    handleUserMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        height: `${theme.custom.navbarHeight}px`,
        background: openNav
          ? alpha(theme.palette.primary.main, 0.55)
          : `linear-gradient(
          180deg,
          ${alpha(theme.palette.primary.main, 0.95)} 0%,
          ${alpha(theme.palette.primary.main, 0.85)} 100%
        )`,
        backdropFilter: openNav ? "blur(16px)" : "blur(10px)",
        WebkitBackdropFilter: openNav ? "blur(16px)" : "blur(10px)",
        borderBottom: `1px solid ${alpha(
          theme.palette.secondary.main,
          openNav ? 0.18 : 0.25
        )}`,
        boxShadow: openNav
          ? `0 1px 0 ${alpha(theme.palette.common.black, 0.2)}`
          : `0 6px 20px ${alpha(theme.palette.primary.dark, 0.25)}`,
        px: theme.spacing(2),
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: `${theme.custom.navbarHeight}px`,
          height: `${theme.custom.navbarHeight}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        {/* LEFT: Icon circle (A on dashboard, ← elsewhere) + ATHENA RWE wordmark */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {location.pathname === "/dashboard" ? (
            <Box
              data-tour="athena-logo"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: alpha(theme.palette.primary.dark, 0.3),
                backdropFilter: "blur(6px)",
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
                boxShadow: theme.custom.glassShadow,
                cursor: "pointer",
                transition: "background 0.2s ease",
                "&:hover": {
                  background: alpha(theme.palette.primary.dark, 0.45),
                  borderColor: theme.palette.secondary.main,
                },
              }}
              onClick={() => navigate("/dashboard", { state: { resetView: Date.now() } })}
            >
              <Box
                component="img"
                src="/brand/icon/a_white_path.svg"
                alt="Athena"
                sx={{ height: 16, width: "auto", display: "block", opacity: 0.7,
                  "&:hover": {
                    opacity: 1.0
                  },
                }}
              />
            </Box>
          ) : (
            <IconButton
              data-tour="athena-logo"
              onClick={() => navigate(-1)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: alpha(theme.palette.primary.dark, 0.3),
                color: theme.palette.secondary.main,
                backdropFilter: "blur(6px)",
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
                boxShadow: theme.custom.glassShadow,
                "&:hover": {
                  background: alpha(theme.palette.primary.dark, 0.45),
                  borderColor: theme.palette.secondary.main,
                },
              }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          )}

          {/* ATHENA RWE wordmark */}
          <Box
            data-tour="athena-logo"
            component="img"
            src="/brand/inline/athena_rwe_white_path.svg"
            alt="Athena RWE"
            onClick={() => navigate("/dashboard", { state: { resetView: Date.now() } })}
            sx={{
              display: { xs: "none", sm: "block" },
              height: 24,
              width: "auto",
              cursor: "pointer",
              opacity: 0.9,
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: 1 },
            }}
          />
        </Box>

        {/* CENTER-LEFT: patient typeahead, on the patient-details path only.
            Deep inside one patient there is otherwise no way to reach another
            without going back to the dashboard first. See NavPatientSearch for
            why it is a dropdown rather than a redirect. */}
        {navSearch && <NavPatientSearch />}

        {/* CENTER: task progress (only visible when active) */}
        {hasActiveTasks && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <LinearProgress
              variant={taskProgress >= 0 ? "determinate" : "indeterminate"}
              value={taskProgress >= 0 ? taskProgress : undefined}
              sx={{
                width: 100,
                height: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.common.white, 0.08),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`,
                },
              }}
            />
          </Box>
        )}
        {/* RIGHT: Language + User */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Language Selector */}
          <IconButton
            onClick={handleLangMenuOpen}
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: alpha(theme.palette.secondary.main, 0.15),
              color: theme.palette.secondary.main,
              fontWeight: "bold",
              fontSize: "0.75rem",
              "&:hover": {
                background: alpha(theme.palette.secondary.main, 0.25),
              },
            }}
          >
            {language.toUpperCase()}
          </IconButton>

          {/* Contracts Status */}
          <ContractsNavChip />

          {/* Notification Bell */}
          <NotificationBell onActiveTasksChange={handleActiveTasksChange} />

          {/* User Avatar */}
          <IconButton
            onClick={handleUserMenuOpen}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: alpha(theme.palette.primary.dark, 0.3),
              color: theme.palette.secondary.main,
              fontWeight: 700,
              backdropFilter: "blur(6px)",
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
              boxShadow: theme.custom.glassShadow,
              "&:hover": {
                background: alpha(theme.palette.primary.dark, 0.45),
                borderColor: theme.palette.secondary.main,
              },
            }}
          >
            {userInitial}
          </IconButton>
        </Box>

        {/* USER MENU */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          TransitionComponent={Fade}
          transitionDuration={200}
        >
          {/* Admin Panel — system admin only (top of menu for visibility) */}
          {isSystemAdmin && (
            <MenuItem
              onClick={() => {
                navigate("/superadmin");
                handleUserMenuClose();
              }}
            >
              <ListItemIcon>
                <AdminPanelIcon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText>{t("nav.admin_panel", "Admin Panel")}</ListItemText>
            </MenuItem>
          )}

          <MenuItem
            onClick={() => {
              navigate("/billing");
              handleUserMenuClose();
            }}
          >
            <ListItemIcon>
              <SettingsIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText>{t("nav.settings", "Account Settings")}</ListItemText>
          </MenuItem>

          {/* Environments – tier-gated, but env gatekeepers (system admins
              + is_environment_admin) always have access regardless of tier
              so they can manage memberships. Joan, 2026-05-28. */}
          {canManageTeam || isEnvGatekeeper ? (
            <MenuItem
              onClick={() => {
                navigate("/environments");
                handleUserMenuClose();
              }}
            >
              <ListItemIcon>
                <EnvironmentsIcon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText>{t("nav.environments", "Environments")}</ListItemText>
            </MenuItem>
          ) : (
            <Tooltip
              title={t(
                "tier_gate.environments_menu",
                "Upgrade to Professional to manage team environments and invite collaborators.",
              )}
              placement="left"
              arrow
            >
              <span>
                <MenuItem disabled>
                  <ListItemIcon>
                    <LockIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                  </ListItemIcon>
                  <ListItemText
                    sx={{ "& .MuiTypography-root": { color: "text.disabled" } }}
                  >
                    {t("nav.environments", "Environments")}
                  </ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
          
          {/* Legal Setup — shown when complete (otherwise it's in the navbar) */}
          {!isSystemAdmin && contractsStatus.overallStatus === "complete" && (
            <MenuItem
              onClick={() => {
                setLegalModalOpen(true);
                handleUserMenuClose();
              }}
            >
              <ListItemIcon>
                <VerifiedUserIcon sx={{ fontSize: 18, color: "success.main" }} />
              </ListItemIcon>
              <ListItemText>{t("nav.legal_setup", "Legal Setup")}</ListItemText>
            </MenuItem>
          )}

          {/* Theme Submenu Trigger — temporarily hidden
          <MenuItem onClick={handleThemeMenuOpen}>
            <ListItemIcon>
              <PaletteIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText>{t("nav.theme", "Theme")}</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {themeOptions.find(t => t.value === themeVariant)?.label.replace("Athena ", "")}
            </Typography>
          </MenuItem>
          */}

          {/* Quick Tutorial — disabled while the flow is broken. See
              TUTORIAL_ENABLED in hooks/useOnboardingTutorial. */}
          <MenuItem
            disabled={!TUTORIAL_ENABLED}
            onClick={() => {
              navigate("/dashboard");
              tutorial.start();
              handleUserMenuClose();
            }}
          >
            <ListItemIcon>
              <TutorialIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText>{t("tutorial.menu_item", "Quick Tutorial")}</ListItemText>
          </MenuItem>
          
          <Divider sx={{ my: 1 }} />
          
          <MenuItem
            onClick={() => {
              handleLogout();
              handleUserMenuClose();
            }}
          >
            <ListItemIcon>
              <LogoutIcon sx={{ fontSize: 18, color: "error.main" }} />
            </ListItemIcon>
            <ListItemText>
              <strong>{t("auth.logout")}</strong>
            </ListItemText>
          </MenuItem>
        </Menu>

        {/* THEME MENU */}
        <Menu
          anchorEl={themeMenuAnchor}
          open={Boolean(themeMenuAnchor)}
          onClose={handleThemeMenuClose}
          TransitionComponent={Fade}
          transitionDuration={200}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t("nav.select_theme", "SELECT THEME")}
            </Typography>
          </Box>
          {themeOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => changeTheme(option.value)}
              selected={themeVariant === option.value}
              sx={{
                minWidth: 200,
              }}
            >
              <ListItemIcon>
                {themeVariant === option.value ? (
                  <CheckIcon sx={{ fontSize: 18, color: "success.main" }} />
                ) : (
                  <Box sx={{ width: 18 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={option.label}
                secondary={option.description}
                primaryTypographyProps={{ fontWeight: themeVariant === option.value ? 600 : 400 }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </MenuItem>
          ))}
        </Menu>

        {/* LANGUAGE MENU */}
        <Menu
          anchorEl={langMenuAnchor}
          open={Boolean(langMenuAnchor)}
          onClose={handleLangMenuClose}
          TransitionComponent={Fade}
          transitionDuration={200}
          PaperProps={{
            sx: {
              maxHeight: 400,
              minWidth: 220,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: athenaColors.periwinkle,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {t("nav.select_language", "SELECT LANGUAGE")}
            </Typography>
          </Box>
          {languages.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              selected={language === lang.code}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {language === lang.code ? (
                  <CheckIcon sx={{ fontSize: 18, color: "success.main" }} />
                ) : (
                  <Box sx={{ width: 18 }} />
                )}
              </ListItemIcon>
              <Box sx={{ fontWeight: 600, fontSize: "0.8rem", minWidth: 28 }}>
                {lang.code.toUpperCase()}
              </Box>
              <ListItemText
                primary={lang.nativeName}
                secondary={lang.name !== lang.nativeName ? lang.name : undefined}
                primaryTypographyProps={{ 
                  fontWeight: language === lang.code ? 600 : 400,
                  fontSize: "0.9rem",
                }}
                secondaryTypographyProps={{
                  variant: "caption",
                  sx: { color: athenaColors.periwinkle },
                }}
              />
              {lang.direction === "rtl" && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  RTL
                </Typography>
              )}
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>
      {legalModalOpen && (
        <ContractsWizardModal
          open={legalModalOpen}
          onClose={() => setLegalModalOpen(false)}
        />
      )}

      {/* Progress bar is now rendered under the logo in the center */}
    </AppBar>
  );
};

export default TopNavBarApp;
